// EisenhowerScoring - Eisenhower Matrix scoring interface (Categorical)
// Implements US-3.3: Matrix Scoring Frameworks (Value/Effort, Eisenhower, Risk Assessment)
// NOTE: Eisenhower uses categorical dropdowns (not numeric), true to original method

import React, { useState, useEffect, useCallback } from 'react';
import styles from './MoscowScoring.module.css';
import { Item, Score, CalculatedResult } from '../types/domain';
import { updateScores } from '../services/scoreService';

interface EisenhowerScoringProps {
  item: Item;
  isReadOnly: boolean;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

/**
 * Eisenhower Matrix scoring component (Categorical)
 * - Displays 2 dropdown fields: Urgency (Urgent/Not Urgent), Importance (Important/Not Important)
 * - Calculates and displays quadrant: Do First, Schedule, Delegate, Eliminate
 * - Auto-saves on value change
 */
export const EisenhowerScoring: React.FC<EisenhowerScoringProps> = ({
  item,
  isReadOnly,
  onScoresUpdated,
}) => {
  // EXTRACT current scores from item
  const getScoreValue = (criterion: string): string => {
    const score = item.scores?.find((s) => s.criterion === criterion);
    return score ? String(score.value) : '';
  };

  // STATE for score selections
  const [urgency, setUrgency] = useState<string>(getScoreValue('urgency'));
  const [importance, setImportance] = useState<string>(getScoreValue('importance'));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UPDATE local state when item scores change (real-time updates from other users)
  useEffect(() => {
    setUrgency(getScoreValue('urgency'));
    setImportance(getScoreValue('importance'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scores]);

  /**
   * CALCULATE quadrant locally for immediate feedback
   * Quadrants: Do First, Schedule, Delegate, Eliminate
   * NOTE: No useCallback needed - function is only called inline, not passed as prop
   */
  const calculateQuadrant = (u: string, i: string): string | null => {
    if (!u || !i) return null;

    if (u === 'urgent' && i === 'important') return 'Do First';
    if (u === 'not-urgent' && i === 'important') return 'Schedule';
    if (u === 'urgent' && i === 'not-important') return 'Delegate';
    if (u === 'not-urgent' && i === 'not-important') return 'Eliminate';
    return null;
  };

  const calculatedQuadrant = calculateQuadrant(urgency, importance);

  /**
   * SAVE scores to backend
   */
  const saveScores = useCallback(
    async (u: string, i: string) => {
      // VALIDATE: Only save if both values are set
      if (!u || !i) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);

        const response = await updateScores(item.id, [
          { criterion: 'urgency', value: u },
          { criterion: 'importance', value: i },
        ]);

        if (!response.success) {
          throw new Error(response.error || 'Failed to save scores');
        }

        // NOTIFY parent component of score update
        if (onScoresUpdated && response.scores && response.calculatedResult) {
          onScoresUpdated(item.id, response.scores, response.calculatedResult);
        }
      } catch (err) {
        console.error('Failed to save scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to save scores');
      } finally {
        setIsSaving(false);
      }
    },
    [item.id, onScoresUpdated]
  );

  /**
   * AUTO-SAVE when both values are selected
   */
  useEffect(() => {
    if (!isReadOnly && urgency && importance) {
      saveScores(urgency, importance);
    }
  }, [urgency, importance, isReadOnly, saveScores]);

  /**
   * HANDLE dropdown change
   */
  const handleUrgencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUrgency(e.target.value);
  };

  const handleImportanceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setImportance(e.target.value);
  };

  /**
   * GET quadrant color class for visual distinction
   */
  const getQuadrantClass = (quadrant: string | null): string => {
    if (!quadrant) return '';
    switch (quadrant) {
      case 'Do First':
        return styles.quadrantDoFirst || ''; // Red
      case 'Schedule':
        return styles.quadrantSchedule || ''; // Green
      case 'Delegate':
        return styles.quadrantDelegate || ''; // Yellow
      case 'Eliminate':
        return styles.quadrantEliminate || ''; // Gray
      default:
        return '';
    }
  };

  return (
    <div className={styles.moscowScoring}>
      <div className={styles.eisenhowerGrid}>
        {/* URGENCY dropdown */}
        <div className={styles.categoryField}>
          <label htmlFor={`urgency-${item.id}`}>Dringlichkeit</label>
          <select
            id={`urgency-${item.id}`}
            value={urgency}
            onChange={handleUrgencyChange}
            disabled={isReadOnly || isSaving}
            className={styles.categorySelect}
          >
            <option value="">Auswählen...</option>
            <option value="urgent">Dringlich</option>
            <option value="not-urgent">Nicht dringlich</option>
          </select>
        </div>

        {/* IMPORTANCE dropdown */}
        <div className={styles.categoryField}>
          <label htmlFor={`importance-${item.id}`}>Wichtigkeit</label>
          <select
            id={`importance-${item.id}`}
            value={importance}
            onChange={handleImportanceChange}
            disabled={isReadOnly || isSaving}
            className={styles.categorySelect}
          >
            <option value="">Auswählen...</option>
            <option value="important">Wichtig</option>
            <option value="not-important">Nicht wichtig</option>
          </select>
        </div>
      </div>

      {/* CALCULATED QUADRANT display */}
      {calculatedQuadrant && (
        <div className={`${styles.categoryDisplay} ${getQuadrantClass(calculatedQuadrant)}`}>
          <span className={styles.categoryLabel}>Quadrant:</span>
          <span className={styles.categoryValue}>{calculatedQuadrant}</span>
          {isSaving && <span className={styles.savingIndicator}>Speichere...</span>}
        </div>
      )}

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* UNSCORED indicator */}
      {!calculatedQuadrant && !isReadOnly && (
        <div className={styles.unscoredMessage}>Wähle beide Kriterien aus, um den Quadrant zu bestimmen</div>
      )}
    </div>
  );
};
